import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Maximize2, Minimize2, Bot, BarChart4, PiggyBank, TrendingUp, Brain } from 'lucide-react';

const AIPocket = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Array<{ type: 'user' | 'ai'; content: string }>>([]);
  const [greeting, setGreeting] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const chatRef = useRef<HTMLDivElement>(null);

  const DEEPSEEK_API_KEY = import.meta.env.VITE_DEEPSEEK_API_KEY;

  const sendMessage = async (content: string, action?: string) => {
    try {
      setIsLoading(true);
      const userMessage = action ? quickActionPrompts[action] : content;
      
      // Add user message to chat
      setMessages(prev => [...prev, { type: 'user', content: userMessage }]);

      // Prepare context for the AI
      const context = `You are a financial AI assistant named GreenGuard. 
                      You help users with their financial decisions and provide insights.
                      Keep responses concise and focused on financial advice.`;

      const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            { role: 'system', content: context },
            { role: 'user', content: userMessage }
          ]
        })
      });

      const data = await response.json();
      
      // Add AI response to chat
      setMessages(prev => [...prev, { type: 'ai', content: data.choices[0].message.content }]);
      
      // Clear input after sending
      setInputValue('');
      
      // Scroll to bottom of chat
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActionPrompts: Record<string, string> = {
    budget: "Can you analyze my current budget and spending patterns?",
    market: "What are the latest market trends and investment opportunities?",
    save: "Suggest ways to improve my savings based on my spending habits.",
    forecast: "Can you provide an AI forecast of my financial health for next month?"
  };

  const handleQuickAction = (action: string) => {
    sendMessage('', action);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
    }
  };

  const quickActions = [
    { icon: <BarChart4 size={20} />, label: 'Budget Check', action: 'budget' },
    { icon: <TrendingUp size={20} />, label: 'Market Trends', action: 'market' },
    { icon: <PiggyBank size={20} />, label: 'Save More', action: 'save' },
    { icon: <Brain size={20} />, label: 'AI Forecast', action: 'forecast' }
  ];

  useEffect(() => {
    const hour = new Date().getHours();
    setGreeting(
      hour < 12 ? 'Good morning' :
      hour < 18 ? 'Good afternoon' :
      'Good evening'
    );
  }, []);

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-[#68A511] text-white p-4 rounded-full shadow-lg hover:bg-[#527f0e] transition-colors z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Bot size={24} />
      </motion.button>

      {/* AI Pocket Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className={`fixed ${isExpanded ? 'top-4 right-4 left-4 bottom-4' : 'bottom-20 right-6'} 
              bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-2xl 
              ${isExpanded ? 'w-auto' : 'w-[380px]'} overflow-hidden z-40`}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white/50 dark:bg-gray-800/50">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-white">{greeting}, Michael</h3>
                <p className="text-sm text-[#68A511]">Your finances are looking healthy today!</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  {isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 p-4">
              {quickActions.map((action) => (
                <button
                  key={action.action}
                  onClick={() => handleQuickAction(action.action)}
                  className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-[#68A511] hover:text-white transition-colors"
                  disabled={isLoading}
                >
                  {action.icon}
                  <span className="text-sm font-medium">{action.label}</span>
                </button>
              ))}
            </div>

            {/* Chat Area */}
            <div ref={chatRef} className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    message.type === 'user' 
                      ? 'bg-[#68A511] text-white' 
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {message.content}
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                    <span className="animate-pulse">Thinking...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about your finances..."
                  className="flex-1 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50"
                  disabled={isLoading}
                />
                <button 
                  type="submit" 
                  className="p-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] disabled:opacity-50"
                  disabled={isLoading || !inputValue.trim()}
                >
                  <MessageSquare size={20} />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIPocket; 